// TODO: duplicated from MV libs/accelerate/dash-store/src/selectors/printPdfDealSelectors.ts (getPdfVehicle, getPdfDealer, getPdfShopper, getCoBuyerName, getPdfTradeIns); move to a shared location used by MV and SCI
import type { PdfDealer, PdfPerson, PdfTradeIn, PdfVehicle } from '@makemydeal/dr-platform-types';
import type { Consumer, DealXG } from '@makemydeal/dr-shared-bff-types';

import type {
    ProposalDealerInput,
    ProposalFinancialDetails,
    ProposalSellingVehicle,
    ProposalTradeIn
} from './types/proposalMapper';
import { VEHICLE_CONDITIONS } from './constants/proposalConstants';

const isTradeInEmpty = (trade: PdfTradeIn): boolean =>
    !trade.year &&
    !trade.make &&
    !trade.model &&
    !trade.reconditionCost &&
    !trade.trim &&
    !trade.value &&
    !trade.vin &&
    !trade.mileage &&
    !trade.payoff;

// TODO: reuse between SCI and MV libs/accelerate/dash-store/src/selectors/printPdfDealSelectors.ts (getPdfTradeIns)
const mapTradeInToPdfTradeIn = (trade: ProposalTradeIn): PdfTradeIn => ({
    year: (trade.tradeVehicle?.year || '').toString(),
    make: trade.tradeVehicle?.make || '',
    model: trade.tradeVehicle?.model || '',
    trim: trade.tradeVehicle?.trim || '',
    value: trade.tradeAllowance || 0,
    vin: trade.tradeVehicle?.vin?.toUpperCase() || '',
    mileage: trade.tradeVehicle?.odometer || 0,
    reconditionCost: trade.tradeReconditionDetails?.tradeReconditionTotal || 0,
    payoff: trade.tradePayoffDetails?.tradePayoffAmount || 0
});

export const getIncludedTradeInRecords = (deal: DealXG): ProposalTradeIn[] =>
    ((deal.dealStructure?.vehicles?.tradeInVehicles?.tradeIns ?? []) as ProposalTradeIn[]).filter(
        (trade) => trade?.isTradeIncluded
    );

export const mapProposalTradeIns = (deal: DealXG): { trades: PdfTradeIn[]; tradesMap: Record<string, PdfTradeIn> } => {
    const allTrades = (deal.dealStructure?.vehicles?.tradeInVehicles?.tradeIns ?? []) as ProposalTradeIn[];
    const trades: PdfTradeIn[] = [];
    const tradesMap: Record<string, PdfTradeIn> = {};
    allTrades.forEach((trade, originalIndex) => {
        const mapped = mapTradeInToPdfTradeIn(trade);
        if (isTradeInEmpty(mapped)) {
            return;
        }
        trades.push(mapped);
        tradesMap[`trade${originalIndex}`] = mapped;
    });
    return { trades, tradesMap };
};

export const mapProposalSalesPerson = (deal: DealXG): PdfPerson | undefined => {
    const salesPersonName = deal.employees?.find((employee) => employee.employeeRole === 'PrimarySalesperson')?.name;
    return salesPersonName && { firstName: salesPersonName.firstName ?? '', lastName: salesPersonName.lastName ?? '' };
};

export const mapProposalDealer = (deal: DealXG, dealer: ProposalDealerInput): PdfDealer => {
    const salesPerson = mapProposalSalesPerson(deal);
    return {
        id: dealer.id,
        name: dealer.name,
        street: dealer.street,
        city: dealer.city,
        state: dealer.state,
        zip: dealer.zip,
        phoneNumber: dealer.phoneNumber ?? '',
        sponsor: deal.references?.find((reference) => reference.name === 'sponsor')?.value ?? '',
        ...(salesPerson && { salesPerson })
    };
};

export const mapProposalShopper = (deal: DealXG): PdfPerson => {
    const buyer = (deal.dealStructure?.participants?.buyer ?? {}) as Partial<Consumer>;
    const address = buyer.address ?? {};
    return {
        firstName: buyer.firstName ?? '',
        lastName: buyer.lastName ?? '',
        ...(buyer.customerType && { customerType: buyer.customerType }),
        ...(buyer.legalName && { business: { legalName: buyer.legalName } }),
        phone: buyer.phoneNumber ?? buyer.cellPhone ?? buyer.homePhone ?? buyer.workPhone ?? '',
        email: buyer.emailAddress ?? '',
        streetAddress: address.addressLine1 ?? '',
        streetAddress2: address.addressLine2 ?? '',
        city: address.city ?? '',
        state: address.stateProvince ?? '',
        country: address.country ?? '',
        zip: address.postalCode ?? ''
    };
};

// TODO: make a small name util usable on client and server; MV libs/accelerate/dash-store/src/selectors/printPdfDealSelectors.ts (getCoBuyerName)
export const mapProposalCoBuyerName = (deal: DealXG): string | undefined => {
    const coBuyer = deal.dealStructure?.participants?.coBuyer;
    const name = `${coBuyer?.firstName || ''} ${coBuyer?.lastName || ''}`.trim();
    return name || undefined;
};

const formatFuelEconomy = (vehicle: ProposalSellingVehicle): string => {
    const numbers = vehicle.technicalSpecifications?.fuelEconomy?.fuelEconomyNumbers;
    const city = numbers?.city ? numbers.city.toFixed(1) : '';
    const highway = numbers?.highway ? numbers.highway.toFixed(1) : '';
    return city && highway ? `${city} / ${highway}` : city || highway;
};

export const mapProposalVehicle = (deal: DealXG): PdfVehicle => {
    const vehicle = (deal.dealStructure?.vehicles?.sellingVehicle ?? {}) as ProposalSellingVehicle;
    const fd = (deal.dealStructure?.financialDetails ?? {}) as ProposalFinancialDetails;
    const condition = (vehicle.inventoryType ?? '').toLowerCase() as PdfVehicle['condition'];
    return {
        condition: VEHICLE_CONDITIONS.includes(condition) ? condition : ('' as PdfVehicle['condition']),
        year: Number(vehicle.year) || 0,
        make: vehicle.make ?? '',
        model: vehicle.model ?? '',
        trim: vehicle.trim ?? '',
        vin: vehicle.vin ?? '',
        stockNumber: vehicle.stockNumber ?? '',
        mileage: vehicle.odometerMileage ?? 0,
        color: vehicle.exteriorColor ?? '',
        engine: vehicle.powertrain?.engineDetails?.description ?? '',
        fuelEconomy: formatFuelEconomy(vehicle),
        retailPrice: fd.priceDetails?.retailPrice ?? 0,
        urls: { imageUrl: vehicle.vehicleImage ?? '' }
    };
};
